import { ReactImageTurntable } from 'react-image-turntable';
import { useState, useEffect } from 'react';

import DetectOutsideClick from '../helpers/DetectOutsideClick';
import ProgressBar from './ProgressBar';

import { shiftArrayElements } from '../../helpers/shared';
import { ANIMATE_FRAME_REFRESH_INTERVAL } from '../../constants/ui';

const Preview3D: React.FC<any> = (props) => {
  const { animationFrameUrls, posterImage } = props;

  const [turntableImagesArray, setTurntableImagesArray] = useState(null);
  const [pausePreviewAnimation, setPausePreviewAnimation] = useState<boolean>(false);
  const [turntableGrabbed, setTurntableGrabbed] = useState<boolean>(false);

  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [imagesLoaded, setImagesLoaded] = useState<boolean>(false);
  const [loadedImagesCounter, setLoadedImagesCounter] = useState<number>(0);
  const [error, setError] = useState<string>('');

  const loadImages = (): void => {
    const loadImage = (image: string) => {
      return new Promise((resolve, reject) => {
        const loadImg = new Image();
        loadImg.src = image;
        loadImg.onload = () => {
          resolve(image);
          setLoadedImagesCounter((count) => count + 1);
        };

        loadImg.onerror = (err) => reject(err);
      });
    };

    setError('');
    setLoadedImagesCounter(0);
    setLoadingImages(true);
    Promise.all(animationFrameUrls.map((image: string) => loadImage(image)))
      .then(() => {
        setLoadingImages(false);
        setImagesLoaded(true);
      })
      .catch((err) => {
        setError('Model failed to load. Please try again.');
        setLoadingImages(false);
      });
  };

  useEffect(() => {
    if (!animationFrameUrls) return;
    setTurntableImagesArray(JSON.parse(JSON.stringify(animationFrameUrls)));

    loadImages();
  }, []);

  useEffect(() => {
    if (!turntableImagesArray || !!pausePreviewAnimation) return;
    const intervalId = setInterval(() => {
      setTurntableImagesArray(shiftArrayElements(turntableImagesArray));
    }, ANIMATE_FRAME_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [turntableImagesArray, pausePreviewAnimation]);

  const onTurntableGrabChange = (grabbed: boolean) => {
    setTurntableGrabbed(grabbed);
    grabbed
      ? document.body.style.setProperty('cursor', 'grabbing', 'important')
      : document.body.style.removeProperty('cursor');
  };

  const getLoaderPercentage = (): number => {
    if (!turntableImagesArray) return 0;
    if (imagesLoaded) return 100;
    return (loadedImagesCounter / turntableImagesArray.length) * 100;
  };

  return (
    <div className="relative z-40">
      {turntableImagesArray && imagesLoaded && (
        <>
          <DetectOutsideClick
            onOutsideClick={() => onTurntableGrabChange(false)}
            eventName="mouseup"
          >
            <ReactImageTurntable
              className={turntableGrabbed ? 'cursor-grabbing' : 'cursor-grab'}
              images={turntableImagesArray}
              onMouseDown={() => {
                onTurntableGrabChange(true);
                setPausePreviewAnimation(true);
              }}
              onMouseUp={() => onTurntableGrabChange(false)}
              onTouchStart={() => {
                onTurntableGrabChange(true);
                setPausePreviewAnimation(true);
              }}
              onTouchEnd={() => onTurntableGrabChange(false)}
            />
          </DetectOutsideClick>
          <div className="absolute top-4 left-4 bg-blue-1000 rounded-1 text-white text-[16px] rounded-[5px] leading-none p-[6px] z-20">
            360
          </div>
          <div className="absolute max-md:hidden w-full bg-[rgba(0,0,0,.6)] text-white text-[16px] bottom-0 z-8 p-4 flex items-center justify-center gap-3">
            &larr; Click and drag to animate &rarr;
          </div>
        </>
      )}
      {loadingImages && (
        <>
          <div className="w-full pb-[56.25%] bg-[#000]">
            <img
              className="absolute top-0 bottom-0 left-0 right-0 opacity-30"
              src={posterImage || ''}
            />
            <div className="absolute w-full left-0 bottom-[42px]">
              <ProgressBar
                className=""
                bgColor="#000"
                progressPct={getLoaderPercentage()}
                cornered={true}
              />
            </div>
          </div>
          <div className="absolute w-full bg-[rgba(0,0,0,.6)] text-white text-[16px] bottom-0 z-8 p-4 flex items-center justify-center gap-3">
            Loading 3D model preview, please wait
          </div>
        </>
      )}
      {error && (
        <div className="w-full pb-[56.25%] bg-[#000] relative z-10">
          <img
            className="absolute top-0 bottom-0 left-0 right-0 opacity-10"
            src={posterImage || ''}
          />
          <div className="absolute inset-0 flex flex-col justify-center items-center text-white">
            <span className="text-[24px]">Something went wrong</span>
            <span className="text-[18px]">{error}</span>
            <button
              className="bg-a-blue text-white p-3 eurostile font-medium text-[16px] rounded-[5px] uppercase mt-4"
              onClick={() => loadImages()}
            >
              retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Preview3D;
